import { AnimationController, loadPixi, type ImageLoader, type PixiModule } from '../animation/animationController';
import { SessionClock } from '../av/sessionClock';
import { fitCamera } from '../scene/geometry';
import { snapshotReactions, type AnimationSnapshot, type ExportSnapshot } from './snapshot';
import type { AudioFeatures } from '../visualizer/analysis';
import { sessionVisualizer } from '../visualizer/evaluate';

/**
 * Deterministic frame rendering for video export (Phase 4B): `renderFrame(t)` draws the
 * session exactly as the Sync preview shows it at session position `t`, without starting
 * playback.
 *
 * It is the preview's renderer, not a second animation engine: an AnimationController at
 * the output size (resolution 1, export presentation) follows its own *paused*
 * SessionClock. Seeking that clock makes the controller evaluate keyframes at the frame
 * `frameAtTime` gives for `t`, Phase 0 shapes at `t`, and reactions plus motion cues
 * through the same `sessionReactions` the live controller uses, then draw synchronously.
 * Nothing depends on the previous frame, so frames can be rendered in any order.
 *
 * Phase 5C.1: it also renders an `AnimationSnapshot` (a scene with an animation and no
 * sync session) for visual review. The clock then carries the animation's own timing and
 * no reactor or visualizer is installed; everything else (scene, characters, layers, Look
 * effects at full quality, framing) is the same code path.
 */
export interface FrameSource {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  /** Draws session position `seconds` into `canvas`. */
  renderFrame(seconds: number): void;
  destroy(): void;
}

export class RendererError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RendererError';
  }
}

export interface FrameRendererOptions {
  loadImage?: ImageLoader;
  /** Phase 4C: measured audio features for visualizer layers (null: event-driven fallback). */
  features?: AudioFeatures | null;
  /** Test seam. */
  loadPixi?: () => Promise<PixiModule>;
}

export class SessionFrameRenderer implements FrameSource {
  private readonly clock = new SessionClock(() => 0);
  private readonly controller: AnimationController;
  private readonly container: HTMLDivElement;
  private destroyed = false;

  private constructor(
    private readonly snapshot: ExportSnapshot | AnimationSnapshot,
    readonly width: number,
    readonly height: number,
    options: FrameRendererOptions,
  ) {
    this.clock.setTiming(snapshot.timing);
    this.controller = new AnimationController(options.loadPixi ?? loadPixi, undefined, {
      loadImage: options.loadImage,
      clock: this.clock,
      exportSize: { width, height },
    });
    // A detached element: the export canvas is never part of the page.
    this.container = document.createElement('div');
  }

  /** Mounts the renderer and waits for every image; fails with a readable error. */
  static async create(snapshot: ExportSnapshot | AnimationSnapshot, width: number, height: number, options: FrameRendererOptions = {}): Promise<SessionFrameRenderer> {
    const r = new SessionFrameRenderer(snapshot, width, height, options);
    try {
      await r.controller.mount(r.container, snapshot.scene, snapshot.project.characters);
      const { status, error } = r.controller.snapshot;
      if (status === 'error' || !r.controller.canvas) throw new RendererError(error ?? 'The export renderer could not start.');
      if ('session' in snapshot) {
        r.controller.setReactor((seconds) => snapshotReactions(snapshot, seconds));
        // The visualizer through the same pure function as the preview (Phase 4C).
        if (snapshot.session.visualizer.layers.length) {
          const features = options.features ?? null;
          r.controller.setVisualizer((seconds) => sessionVisualizer(snapshot.project, snapshot.session, snapshot.events, features, seconds));
        }
      }
      r.controller.setAnimation(snapshot.animation);
      r.controller.setCamera(fitCamera(snapshot.scene.width, snapshot.scene.height, width, height, 0));
      const failed = await r.controller.whenImagesReady();
      if (failed.length) {
        const names = failed.map((id) => snapshot.project.assets.find((a) => a.id === id)?.name ?? id);
        throw new RendererError(`The image${names.length > 1 ? 's' : ''} ${names.map((n) => `“${n}”`).join(', ')} could not be loaded for export.`);
      }
      return r;
    } catch (error) {
      r.destroy();
      throw error;
    }
  }

  get canvas(): HTMLCanvasElement {
    const c = this.controller.canvas;
    if (!c) throw new RendererError('The export renderer was destroyed.');
    return c;
  }

  renderFrame(seconds: number): void {
    if (this.destroyed) throw new RendererError('The export renderer was destroyed.');
    // The clock is paused: seeking it re-evaluates and draws this exact position.
    this.clock.seek(seconds);
  }

  /** Values shown at the last rendered frame (tests compare them with the preview's). */
  shown() {
    return { values: this.controller.displayedValues(), effects: this.controller.shownEffects(), frame: this.clock.sample().frame, visualizer: this.controller.shownVisualizer() };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.controller.destroy();
    this.container.replaceChildren();
  }

  /** The sync session rendered, or null for an animation-only snapshot. */
  get session() {
    return 'session' in this.snapshot ? this.snapshot.session : null;
  }
}
